import {AG_GRID_COMPATIBILITY, JustTooltipFormatter, ProgressBarFormatter, TableSorting, useFormatters} from '@wallaby-ui/document';
import {get} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useMemo} from 'react';

import {BasicMeasuresRowType, InterventionsDepartmentReportFilter} from './departmenttypes';

export const useDepartmentBasicMeasuresTableColumns = (
  defaultSort: TableSorting,
  reportFilters: InterventionsDepartmentReportFilter
): Column<BasicMeasuresRowType>[] => {
  const EMPTY = '-';
  const {t, formatFloat} = useFormatters();

  const createFloatFormatter = useCallback(
    (prop: Exclude<keyof BasicMeasuresRowType, symbol>, width: string | undefined = undefined) => {
      const title = t(`department.report.basicmeasures.table.column.${prop}`);
      return {
        field: prop,
        title,
        render: (data: BasicMeasuresRowType) => {
          const propFormatted = formatFloat(data[prop]);
          return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={propFormatted ?? EMPTY} tooltipOverride={propFormatted} />;
        },
        width,
        sorting: false,
        tooltip:
          prop === 'uncheckedHighRiskHospCount'
            ? t(`department.report.basicmeasures.table.column.uncheckedHighRiskHospCount.tooltip`)
            : title,
      };
    },
    [formatFloat, t]
  );

  const createPercentageFormatter = useCallback(
    (prop: Exclude<keyof BasicMeasuresRowType, symbol>, width: string | undefined = undefined) => {
      const title = t(`department.report.basicmeasures.table.column.${prop}`);
      return {
        field: prop,
        title,
        render: (data: BasicMeasuresRowType) => {
          const percent = data[prop];
          const percentString = `${formatFloat(percent)} %`;
          return (
            <ProgressBarFormatter
              {...AG_GRID_COMPATIBILITY}
              value={percentString ?? EMPTY}
              tooltipOverride={percentString}
              progressPercent={percent}
              style={{maxWidth: '150px'}}
            />
          );
        },
        width,
        sorting: false,
      };
    },
    [formatFloat, t]
  );

  return useMemo(() => {
    const columns: Column<BasicMeasuresRowType>[] = [
      {
        field: 'measureLabel',
        title: t('department.report.basicmeasures.table.column.measure'),
        render: (data) => (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={t('department.report.basicmeasures.table.column.measure.value')}
            style={{fontWeight: 'bold'}}
            tooltipOverride={t('department.report.basicmeasures.table.column.measure.value')}
            bold
          />
        ),
        sorting: false,
      },
      createFloatFormatter('hospCount'),
      createFloatFormatter('hospDaysCount'),
      createFloatFormatter('uncheckedHospCount'),
      createFloatFormatter('uncheckedHighRiskHospCount'),
      createFloatFormatter('avgHospDuration'),
      createFloatFormatter('avgHospDurationDrp'),
      createFloatFormatter('avgHospDurationNoDrp'),
      createPercentageFormatter('acceptanceRate'),
    ];

    return columns.map((column) => ({
      ...column,
      sorting: column.sorting ?? false,
      customSort: (row1, row2, _type) => {
        const field = column.field ?? '';
        const data1 = get(row1, field);
        const data2 = get(row2, field);
        return +(data1 > data2) - +(data1 < data2);
      },
      ...(column.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
    }));
  }, [t, createFloatFormatter, createPercentageFormatter, defaultSort.field, defaultSort.direction]);
};
